import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { Notifications, Save, Comment } from '@mui/icons-material';

const getDefaultReminderDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return date.toISOString().slice(0, 16);
};

const getMinReminderDate = () => new Date().toISOString().slice(0, 16);

const INITIAL_FORM = {
  comment: '',
  enableNotification: false,
  reminderDate: '',
};

const AddActivityDialog = ({ open, onClose, onSubmit, isPending, error, variant = 'activity' }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const isComment = variant === 'comment';

  useEffect(() => {
    if (open) {
      setForm(
        isComment
          ? INITIAL_FORM
          : {
              comment: '',
              enableNotification: true,
              reminderDate: getDefaultReminderDate(),
            }
      );
    }
  }, [open, variant, isComment]);

  const handleClose = () => {
    if (!isPending) {
      setForm(INITIAL_FORM);
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.comment.trim()) return;
    if (form.enableNotification && !form.reminderDate) return;

    try {
      await onSubmit({
        type: form.enableNotification ? 'REMINDER' : 'NOTE',
        comment: form.comment.trim(),
        remindAt: form.enableNotification
          ? new Date(form.reminderDate).toISOString()
          : undefined,
      });
      setForm(INITIAL_FORM);
      onClose();
    } catch {
      // error displayed via error prop
    }
  };

  const handleNotificationToggle = (checked) => {
    setForm((prev) => ({
      ...prev,
      enableNotification: checked,
      reminderDate: checked ? getDefaultReminderDate() : '',
    }));
  };

  const isSubmitDisabled =
    isPending ||
    !form.comment.trim() ||
    (form.enableNotification && !form.reminderDate);

  const DialogIcon = isComment ? Comment : Notifications;
  const dialogTitle = isComment ? 'Dodaj komentarz' : 'Dodaj przypomnienie';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DialogIcon color="primary" />
          {dialogTitle}
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Komentarz"
            value={form.comment}
            onChange={(e) => setForm((prev) => ({ ...prev, comment: e.target.value }))}
            placeholder="Wpisz komentarz..."
            autoFocus
            size="small"
            sx={{ mt: 1 }}
          />

          <FormControlLabel
            sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={form.enableNotification}
                onChange={(e) => handleNotificationToggle(e.target.checked)}
              />
            }
            label={
              <Box>
                <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                  Włącz powiadomienie
                </Box>
                <Box component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  Utworzy powiadomienie e-mail
                </Box>
              </Box>
            }
          />

          {form.enableNotification && (
            <TextField
              fullWidth
              required
              type="datetime-local"
              label="Data przypomnienia"
              value={form.reminderDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, reminderDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getMinReminderDate() }}
              size="small"
              sx={{ mt: 2 }}
              helperText="Wybierz datę i godzinę, kiedy ma nastąpić przypomnienie"
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isPending}>
            Anuluj
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitDisabled}
            startIcon={isPending ? <CircularProgress size={16} /> : <Save />}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddActivityDialog;
